<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useWhatsAppTemplatePicker } from 'dashboard/composables/useWhatsAppTemplatePicker';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsappTemplate from './WhatsappTemplate.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
  messageMode: {
    type: String,
    default: 'template',
  },
});

const emit = defineEmits(['sendMessage', 'update:messageMode']);

const { t } = useI18n();
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const selectedTemplate = ref(null);
const showTemplatesMenu = ref(false);

const {
  query,
  teamFilter,
  showFavoritesOnly,
  teamFilterOptions,
  filterTemplates,
  isFavorite,
  toggleFavorite,
  processTemplates,
} = useWhatsAppTemplatePicker(() => props.inboxId);

const whatsAppTemplateMessages = computed(() => {
  return processTemplates(getFilteredWhatsAppTemplates.value(props.inboxId));
});

const filteredTemplates = computed(() => {
  return filterTemplates(whatsAppTemplateMessages.value);
});

const getTemplateBody = template => {
  return template.components.find(component => component.type === 'BODY').text;
};

const handleTriggerClick = () => {
  query.value = '';
  teamFilter.value = 'all';
  showFavoritesOnly.value = false;
  emit('update:messageMode', 'template');
  showTemplatesMenu.value = !showTemplatesMenu.value;
};

const handleTemplateClick = template => {
  selectedTemplate.value = template;
  showTemplatesMenu.value = false;
};

const handleBack = () => {
  selectedTemplate.value = null;
  showTemplatesMenu.value = true;
};

const handleSendMessage = template => {
  emit('sendMessage', template);
  selectedTemplate.value = null;
};

const selectPrivateMode = () => {
  selectedTemplate.value = null;
  showTemplatesMenu.value = false;
  emit('update:messageMode', 'private');
};
</script>

<template>
  <div class="relative flex items-center gap-2">
    <Button
      icon="i-ri-whatsapp-line"
      :label="t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.LABEL')"
      :color="messageMode === 'template' ? 'blue' : 'slate'"
      size="sm"
      :disabled="selectedTemplate"
      class="!text-xs font-medium"
      @click="handleTriggerClick"
    />
    <Button
      icon="i-lucide-lock"
      :label="
        t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.PRIVATE_MESSAGE')
      "
      :color="messageMode === 'private' ? 'blue' : 'slate'"
      size="sm"
      class="!text-xs font-medium"
      @click="selectPrivateMode"
    />
    <div
      v-if="showTemplatesMenu"
      class="absolute top-full mt-1.5 max-h-96 overflow-y-auto ltr:left-0 rtl:right-0 flex flex-col gap-2 p-4 items-center w-[21.875rem] h-auto bg-n-solid-2 border border-n-strong shadow-sm rounded-lg"
    >
      <div class="relative w-full">
        <Icon
          icon="i-lucide-search"
          class="absolute size-3.5 top-2 ltr:left-3 rtl:right-3"
        />
        <input
          v-model="query"
          type="search"
          autofocus
          :placeholder="
            t(
              'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.SEARCH_PLACEHOLDER'
            )
          "
          class="w-full h-8 py-2 ltr:pl-10 rtl:pr-10 ltr:pr-2 rtl:pl-2 text-sm reset-base outline-none border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
        />
      </div>
      <div class="flex flex-wrap gap-2 w-full">
        <select
          v-model="teamFilter"
          class="flex-1 min-w-0 h-8 px-2 text-xs rounded-lg bg-n-alpha-black2 text-n-slate-12"
        >
          <option
            v-for="option in teamFilterOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label || t(option.labelKey) }}
          </option>
        </select>
        <button
          class="flex items-center gap-1 h-8 px-2 text-xs rounded-lg"
          :class="
            showFavoritesOnly
              ? 'bg-n-brand/10 text-n-brand'
              : 'bg-n-alpha-black2 text-n-slate-12'
          "
          @click="showFavoritesOnly = !showFavoritesOnly"
        >
          <Icon icon="i-lucide-star" class="size-3" />
        </button>
      </div>
      <div
        v-for="template in filteredTemplates"
        :key="template.id"
        class="flex gap-2 items-start p-2 w-full rounded-lg cursor-pointer dark:hover:bg-n-alpha-3 hover:bg-n-alpha-1"
        @click="handleTemplateClick(template)"
      >
        <button
          class="shrink-0 mt-0.5"
          @click="toggleFavorite(template, $event)"
        >
          <Icon
            icon="i-lucide-star"
            class="size-3.5"
            :class="
              isFavorite(template)
                ? 'text-n-amber-10 fill-n-amber-10'
                : 'text-n-slate-9'
            "
          />
        </button>
        <div class="min-w-0">
          <span class="text-sm text-n-slate-12">{{ template.name }}</span>
          <p class="mb-0 text-xs leading-5 text-n-slate-11 line-clamp-2">
            {{ getTemplateBody(template) }}
          </p>
        </div>
      </div>
      <template v-if="filteredTemplates.length === 0">
        <p class="pt-2 w-full text-sm text-n-slate-11">
          {{ t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.EMPTY_STATE') }}
        </p>
      </template>
    </div>
    <WhatsappTemplate
      v-if="selectedTemplate"
      :template="selectedTemplate"
      :inbox-id="inboxId"
      @send-message="handleSendMessage"
      @back="handleBack"
    />
  </div>
</template>
